import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Trash2 } from 'lucide-react';
import { useDeletePolicy, useFlintProducers, useInsuranceCompanies, useUpdatePolicy } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { SearchableSelect } from '@/components/searchable-select';
import { formatDateInput, formatDollar, fromIsoDate, unformatDollar } from '@/lib/formatters';
import { FieldError } from '@/components/FieldError';
import { POLICY_STATUS_OPTIONS, POLICY_SUBSTATUS_OPTIONS, shouldShowCancellationDate } from './policy-status';
import type { Policy } from '@shared/schema';
import { computeTotalPremium, hasAnyBreakdown } from '@shared/premium';
import { isValidPolicyDate } from '@shared/policy-date';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  policy: Policy;
  onDeleted?: () => void;
}

// CSR-1124 — Payment Plan dropdown options (kept in sync with AddPolicyDialog).
const PAYMENT_PLAN_OPTIONS = [
  { value: 'Paid in Full', label: 'Paid in Full' },
  { value: 'Monthly Payments', label: 'Monthly Payments' },
] as const;

const STATUS_NONE = '__none__';
const SUBSTATUS_NONE = '__none__';
const PAYMENT_NONE = '__none__';

interface FormState {
  policyNumber: string;
  carrier: string;
  effectiveDate: string;
  expirationDate: string;
  status: string;
  substatus: string;
  policyPremium: string;
  brokerFee: string;
  policyFees: string;
  taxes: string;
  commissionPct: string;
  producer: string;
  agency: string;
  mga: string;
  financeCompany: string;
  paymentPlan: string;
  boundDate: string;
  cancellationDate: string;
}

function buildForm(policy: Policy): FormState {
  return {
    policyNumber: policy.policyNumber ?? '',
    carrier: policy.carrier ?? '',
    effectiveDate: fromIsoDate(policy.effectiveDate ?? ''),
    expirationDate: fromIsoDate(policy.expirationDate ?? ''),
    // Status is stored canonical-lowercase (server/sync.ts normalizeStatus), so
    // lowercase on seed so the Select matches a POLICY_STATUS_OPTIONS value.
    status: (policy.status ?? '').toLowerCase(),
    substatus: policy.substatus ?? '',
    // CSR-1604 — hydrate the breakdown from stored components. A synced policy
    // with only a headline `premium` (no breakdown) seeds Policy Premium from
    // it so the live Total matches and an untouched save never zeroes premium.
    policyPremium: policy.policyPremium ?? (hasAnyBreakdown(policy) ? '' : policy.premium ?? ''),
    brokerFee: policy.brokerFee ?? '',
    policyFees: policy.policyFees ?? '',
    taxes: policy.taxes ?? '',
    // CSR-1606 — the stored value only; carrier defaults are a create-time
    // pre-fill and must never overwrite a saved commission on edit.
    commissionPct: policy.commissionPct ?? '',
    producer: policy.producer ?? '',
    agency: policy.agency ?? '',
    mga: policy.mga ?? '',
    financeCompany: policy.financeCompany ?? '',
    paymentPlan: policy.paymentPlan ?? '',
    boundDate: fromIsoDate(policy.boundDate ?? ''),
    cancellationDate: fromIsoDate(policy.cancellationDate ?? ''),
  };
}

function buildUpdateData(form: FormState): Record<string, unknown> {
  const breakdown = {
    policyPremium: form.policyPremium.trim() || null,
    brokerFee: form.brokerFee.trim() || null,
    policyFees: form.policyFees.trim() || null,
    taxes: form.taxes.trim() || null,
  };
  return {
    policyNumber: form.policyNumber.trim(),
    carrier: form.carrier.trim(),
    effectiveDate: form.effectiveDate.trim(),
    expirationDate: form.expirationDate.trim() || null,
    status: form.status.trim() || null,
    substatus: form.substatus || null,
    ...breakdown,
    commissionPct: form.commissionPct.trim() || null,
    premium: computeTotalPremium(breakdown) || null,
    producer: form.producer.trim() || null,
    agency: form.agency.trim() || null,
    mga: form.mga.trim() || null,
    financeCompany: form.financeCompany.trim() || null,
    paymentPlan: form.paymentPlan.trim() || null,
    boundDate: form.boundDate.trim() || null,
    cancellationDate: form.cancellationDate.trim() || null,
  };
}

export function EditPolicyDialog({ open, onOpenChange, policy, onDeleted }: Props) {
  const [form, setForm] = useState<FormState>(() => buildForm(policy));
  const [initialForm, setInitialForm] = useState<FormState>(() => buildForm(policy));
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const update = useUpdatePolicy();
  const deleteMutation = useDeletePolicy();
  const { data: flintProducers } = useFlintProducers();
  const { data: companies } = useInsuranceCompanies();
  const producerOptions = useMemo(
    () => (flintProducers ?? []).map((p) => ({ value: p.fullName, label: p.fullName })),
    [flintProducers],
  );
  const carrierOptions = useMemo(
    () => (companies?.insuranceCarriers ?? []).map((c) => ({ value: c.name, label: c.name })),
    [companies],
  );
  const mgaOptions = useMemo(
    () => (companies?.mgas ?? []).map((m) => ({ value: m.name, label: m.name })),
    [companies],
  );
  const { toast } = useToast();

  // CSR-2358 — seed on the open transition only. `policy` stays in the dep list
  // for the lint rule, but a change to it must NOT re-seed: `usePolicy` inherits
  // the global `refetchOnWindowFocus`, so every return to the tab handed this
  // effect a fresh object and wiped whatever the CSR had typed — the reported
  // "cancellation date keeps reverting to today". `initialForm` is the
  // changed-fields baseline for the save diff, so it must stay anchored to the
  // values the dialog opened with for the same reason.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      const nextForm = buildForm(policy);
      setForm(nextForm);
      setInitialForm(nextForm);
    }
    wasOpen.current = open;
  }, [open, policy]);

  const set = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const setStatusValue = (value: string) =>
    setForm((prev) => ({ ...prev, status: value === STATUS_NONE ? '' : value }));

  const setDate = (field: 'effectiveDate' | 'expirationDate' | 'boundDate' | 'cancellationDate') =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((prev) => ({ ...prev, [field]: formatDateInput(e.target.value) }));

  const setDollar = (field: 'policyPremium' | 'brokerFee' | 'policyFees' | 'taxes') =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((prev) => ({ ...prev, [field]: unformatDollar(e.target.value) }));

  // CSR-1604 — Total recalculates live as the CSR types. Empty breakdown → "".
  const totalPremium = useMemo(
    () =>
      computeTotalPremium({
        policyPremium: form.policyPremium,
        brokerFee: form.brokerFee,
        policyFees: form.policyFees,
        taxes: form.taxes,
      }),
    [form.policyPremium, form.brokerFee, form.policyFees, form.taxes],
  );

  const anyPending = update.isPending || deleteMutation.isPending;

  const effectiveDateError =
    form.effectiveDate.trim() !== '' && !isValidPolicyDate(form.effectiveDate);
  const expirationDateError =
    form.expirationDate.trim() !== '' && !isValidPolicyDate(form.expirationDate);
  const boundDateError =
    form.boundDate.trim() !== '' && !isValidPolicyDate(form.boundDate);
  // One predicate for the field, its error and the submit gate. Computed
  // separately they drifted: a half-typed date left behind by a status change
  // out of the cancellation family kept `canSubmit` false while the input and
  // its FieldError had already unmounted, so Save was dead with nothing on
  // screen saying why.
  const showCancellationDate = shouldShowCancellationDate(form.status);
  const cancellationDateError =
    showCancellationDate &&
    form.cancellationDate.trim() !== '' &&
    !isValidPolicyDate(form.cancellationDate);

  const canSubmit =
    !anyPending &&
    form.policyNumber.trim() !== '' &&
    form.carrier.trim() !== '' &&
    isValidPolicyDate(form.effectiveDate) &&
    !expirationDateError &&
    !boundDateError &&
    !cancellationDateError;

  const handleDelete = () => {
    deleteMutation.mutate(policy.id, {
      onSuccess: () => {
        toast({ title: 'Policy deleted' });
        setDeleteConfirmOpen(false);
        onOpenChange(false);
        onDeleted?.();
      },
      onError: () => {
        toast({ title: 'Failed to delete policy', variant: 'destructive' });
      },
    });
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    const allData = buildUpdateData(form);
    const initialData = buildUpdateData(initialForm);
    // CSR-2358 — the cancellation date is the only field whose visibility depends
    // on another field, so the changed-fields diff has to read the status at both
    // ends of the edit. The baseline always stays the stored value; only the
    // outgoing value follows the current status.
    const shownAtOpen = shouldShowCancellationDate(initialForm.status);
    if (!shownAtOpen && !showCancellationDate) {
      // Never surfaced during this edit, so the CSR expressed no intent about it
      // — leave DC's value alone rather than clearing it as a side effect of an
      // unrelated edit. (DC does emit a cancellation date on a non-terminal
      // status; see the CSR-1626 note in server/.context.md.)
      delete allData.cancellationDate;
      delete initialData.cancellationDate;
    } else if (!showCancellationDate) {
      // Moved out of the cancellation family: clear the date rather than leave a
      // value the CSR can no longer see. Wider than
      // `resolvePolicyStatusChange('reinstatement')`, which only ever lands on
      // `active` — this covers every status outside shouldShowCancellationDate.
      allData.cancellationDate = null;
    }
    const data = Object.fromEntries(
      Object.entries(allData).filter(([field, value]) => initialData[field] !== value),
    );
    update.mutate(
      {
        id: policy.id,
        data: data as any,
      },
      {
        onSuccess: () => {
          toast({ title: 'Policy updated' });
          onOpenChange(false);
        },
        onError: (err: unknown) => {
          const message = err instanceof Error ? err.message : 'Failed to update policy';
          const isDcFailure = message.includes('DC write failed');
          toast({
            title: isDcFailure ? 'Data Center rejected the edit' : 'Failed to update policy',
            description: message,
            variant: 'destructive',
          });
        },
      },
    );
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit policy</DialogTitle>
        </DialogHeader>

        {/* Sync banner */}
        <div
          className="mb-3 rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900"
          data-testid="edit-policy-sync-banner"
        >
          Many policy fields here (status, premium, dates, vehicles/drivers, agent assignments) are
          synced from the carrier. Manual edits apply immediately but may be overwritten on the
          next carrier sync. AMS-only fields (finance company, payment plan, bound date) persist
          locally.
        </div>

        <div className="space-y-3">
          <div>
            <Label htmlFor="edit-policy-number">Policy number *</Label>
            <Input
              id="edit-policy-number"
              data-testid="edit-policy-number"
              placeholder="POL-12345"
              value={form.policyNumber}
              onChange={set('policyNumber')}
            />
          </div>
          <div>
            <Label htmlFor="edit-policy-carrier">Carrier *</Label>
            <SearchableSelect
              value={form.carrier}
              onChange={(v) => setForm((prev) => ({ ...prev, carrier: v }))}
              options={carrierOptions}
              allowCustom
              placeholder="Select or type a carrier…"
            />
            {/* Test-only handle: drives state without going through Radix
                popover (hard to drive in happy-dom). */}
            <input
              type="text"
              className="sr-only"
              tabIndex={-1}
              aria-hidden="true"
              data-testid="edit-policy-carrier"
              value={form.carrier}
              onChange={set('carrier')}
            />
          </div>
          <div>
            <Label htmlFor="edit-policy-effective-date">Effective date *</Label>
            <Input
              id="edit-policy-effective-date"
              data-testid="edit-policy-effective-date"
              placeholder="MM/DD/YYYY"
              inputMode="numeric"
              value={form.effectiveDate}
              onChange={setDate('effectiveDate')}
              className={effectiveDateError ? 'border-destructive' : undefined}
            />
            <FieldError show={effectiveDateError} message="Enter a valid date as MM/DD/YYYY" />
          </div>
          <div>
            <Label htmlFor="edit-policy-expiration-date">Expiration date</Label>
            <Input
              id="edit-policy-expiration-date"
              data-testid="edit-policy-expiration-date"
              placeholder="MM/DD/YYYY"
              inputMode="numeric"
              value={form.expirationDate}
              onChange={setDate('expirationDate')}
              className={expirationDateError ? 'border-destructive' : undefined}
            />
            <FieldError show={expirationDateError} message="Enter a valid date as MM/DD/YYYY" />
          </div>
          <div>
            <Label htmlFor="edit-policy-status">Status</Label>
            <Select
              value={form.status || STATUS_NONE}
              onValueChange={setStatusValue}
            >
              <SelectTrigger id="edit-policy-status" data-testid="edit-policy-status">
                <SelectValue placeholder="Select status…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={STATUS_NONE}>—</SelectItem>
                {POLICY_STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Test-only handle: mirrors the carrier/MGA pattern so component
                tests can change the status without driving Radix's popover,
                which happy-dom cannot open. */}
            <input
              type="text"
              className="sr-only"
              tabIndex={-1}
              aria-hidden="true"
              data-testid="edit-policy-status-value"
              value={form.status}
              onChange={(e) => setStatusValue(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="edit-policy-substatus">Substatus</Label>
            <Select
              value={form.substatus || SUBSTATUS_NONE}
              onValueChange={(v) =>
                setForm((prev) => ({ ...prev, substatus: v === SUBSTATUS_NONE ? '' : v }))
              }
            >
              <SelectTrigger id="edit-policy-substatus" data-testid="edit-policy-substatus">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SUBSTATUS_NONE}>None</SelectItem>
                {POLICY_SUBSTATUS_OPTIONS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {showCancellationDate && (
            <div>
              <Label htmlFor="edit-policy-cancellation-date">Cancellation date</Label>
              <Input
                id="edit-policy-cancellation-date"
                data-testid="edit-policy-cancellation-date"
                placeholder="MM/DD/YYYY"
                inputMode="numeric"
                value={form.cancellationDate}
                onChange={setDate('cancellationDate')}
                className={cancellationDateError ? 'border-destructive' : undefined}
              />
              <FieldError
                show={cancellationDateError}
                message="Enter a valid date as MM/DD/YYYY"
              />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="edit-policy-premium">Policy Premium</Label>
              <Input
                id="edit-policy-premium"
                data-testid="edit-policy-premium"
                inputMode="decimal"
                placeholder="$0"
                value={form.policyPremium ? formatDollar(form.policyPremium) : ''}
                onChange={setDollar('policyPremium')}
              />
            </div>
            <div>
              <Label htmlFor="edit-policy-broker-fee">Broker Fee</Label>
              <Input
                id="edit-policy-broker-fee"
                data-testid="edit-policy-broker-fee"
                inputMode="decimal"
                placeholder="$0"
                value={form.brokerFee ? formatDollar(form.brokerFee) : ''}
                onChange={setDollar('brokerFee')}
              />
            </div>
            <div>
              <Label htmlFor="edit-policy-fees">Finance Charge</Label>
              <Input
                id="edit-policy-fees"
                data-testid="edit-policy-fees"
                inputMode="decimal"
                placeholder="$0"
                value={form.policyFees ? formatDollar(form.policyFees) : ''}
                onChange={setDollar('policyFees')}
              />
            </div>
            <div>
              <Label htmlFor="edit-policy-taxes">Taxes</Label>
              <Input
                id="edit-policy-taxes"
                data-testid="edit-policy-taxes"
                inputMode="decimal"
                placeholder="$0"
                value={form.taxes ? formatDollar(form.taxes) : ''}
                onChange={setDollar('taxes')}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="edit-policy-commission">Commission (%)</Label>
            <Input
              id="edit-policy-commission"
              data-testid="edit-policy-commission"
              inputMode="decimal"
              placeholder="0"
              value={form.commissionPct}
              onChange={set('commissionPct')}
            />
          </div>
          <div>
            <Label htmlFor="edit-policy-total-premium">Total Premium</Label>
            <Input
              id="edit-policy-total-premium"
              data-testid="edit-policy-total-premium"
              readOnly
              tabIndex={-1}
              className="bg-muted"
              value={totalPremium ? formatDollar(totalPremium.replace(/\.00$/, '')) : '$0'}
            />
          </div>
          <div>
            <Label htmlFor="edit-policy-producer">Producer</Label>
            <SearchableSelect
              value={form.producer}
              onChange={(v) => setForm((prev) => ({ ...prev, producer: v }))}
              options={producerOptions}
              allowCustom
              placeholder="Select or type a producer…"
            />
            {/* Test-only handle: drives state without going through Radix
                popover (hard to drive in happy-dom). */}
            <input
              type="text"
              className="sr-only"
              tabIndex={-1}
              aria-hidden="true"
              data-testid="edit-policy-producer"
              value={form.producer}
              onChange={set('producer')}
            />
          </div>
          <div>
            <Label htmlFor="edit-policy-agency">Agency</Label>
            <Input
              id="edit-policy-agency"
              placeholder="Agency name"
              value={form.agency}
              onChange={set('agency')}
            />
          </div>
          <div>
            <Label htmlFor="edit-policy-mga">MGA</Label>
            <SearchableSelect
              value={form.mga}
              onChange={(v) => setForm((prev) => ({ ...prev, mga: v }))}
              options={mgaOptions}
              allowCustom
              placeholder="Select or type an MGA…"
            />
            <input
              type="text"
              className="sr-only"
              tabIndex={-1}
              aria-hidden="true"
              data-testid="edit-policy-mga"
              value={form.mga}
              onChange={set('mga')}
            />
          </div>
          <div>
            <Label htmlFor="edit-policy-finance">Finance company</Label>
            <Input
              id="edit-policy-finance"
              placeholder="Lender name"
              value={form.financeCompany}
              onChange={set('financeCompany')}
            />
          </div>
          <div>
            <Label htmlFor="edit-policy-payment-plan">Payment plan</Label>
            <Select
              value={form.paymentPlan || PAYMENT_NONE}
              onValueChange={(v) =>
                setForm((prev) => ({ ...prev, paymentPlan: v === PAYMENT_NONE ? '' : v }))
              }
            >
              <SelectTrigger id="edit-policy-payment-plan" data-testid="edit-policy-payment-plan">
                <SelectValue placeholder="Select payment plan…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={PAYMENT_NONE}>—</SelectItem>
                {PAYMENT_PLAN_OPTIONS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="edit-policy-bound">Bound date</Label>
            <Input
              id="edit-policy-bound"
              data-testid="edit-policy-bound"
              placeholder="MM/DD/YYYY"
              inputMode="numeric"
              value={form.boundDate}
              onChange={setDate('boundDate')}
              className={boundDateError ? 'border-destructive' : undefined}
            />
            <FieldError show={boundDateError} message="Enter a valid date as MM/DD/YYYY" />
          </div>
        </div>

        {/* CSR-1970 — pill buttons, matching EditInsuredDialog. Layout is
            unchanged; only the corner radius. */}
        <DialogFooter className="flex w-full justify-between">
          <Button
            variant="destructive"
            className="rounded-full cursor-pointer"
            data-testid="edit-policy-delete-btn"
            onClick={() => setDeleteConfirmOpen(true)}
            disabled={anyPending}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="rounded-full cursor-pointer"
              onClick={() => onOpenChange(false)}
              disabled={anyPending}
            >
              Cancel
            </Button>
            <Button
              className="rounded-full cursor-pointer"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {update.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save changes
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete policy?</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete policy {policy.policyNumber}? This action cannot be
            undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="cursor-pointer bg-destructive text-destructive-foreground hover:bg-destructive/90"
            data-testid="edit-policy-delete-confirm-btn"
            onClick={(e) => {
              e.preventDefault();
              handleDelete();
            }}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
